import React from 'react';
import { TargetIcon, TrophyIcon, TrendingUpIcon, ArrowRightIcon, UserIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface RankCardProps {
  badge: string;
  badgeColor: string;
  headerBg: string;
  headerText?: string;
  accent: string;
  accentBg: string;
  accentBorder: string;
  icon: React.ReactNode;
  title: string;
  role: string;
  rows: {
    range: string;
    incentive: string;
    highlight?: boolean;
  }[];
  focus: string;
  growthCallout?: {
    title: string;
    subtitle: string;
  };
}
function RankCard({
  badge,
  badgeColor,
  headerBg,
  accent,
  accentBg,
  accentBorder,
  icon,
  title,
  role,
  rows,
  focus,
  growthCallout
}: RankCardProps) {
  return <div className={`flex-1 bg-white rounded-xl shadow-sm border ${accentBorder} overflow-hidden flex flex-col`} data-id="element-379">
      <div className={`${headerBg} text-white px-5 py-4 flex items-center justify-between gap-3`} data-id="element-380">
        <div className="flex items-center gap-3 min-w-0" data-id="element-381">
          <div className="bg-white/15 p-2 rounded-full shrink-0" data-id="element-382">{icon}</div>
          <h3 className="text-lg font-bold tracking-wide uppercase truncate" data-id="element-383">
            {title}
          </h3>
        </div>
        <span className={`${badgeColor} text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded shrink-0`} data-id="element-384">
          {badge}
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col gap-5" data-id="element-385">
        <div className="flex gap-3" data-id="element-386">
          <TargetIcon className={`w-6 h-6 ${accent} shrink-0`} data-id="element-387" />
          <div data-id="element-388">
            <h4 className={`text-xs font-bold ${accent} uppercase tracking-wider mb-1`} data-id="element-389">
              Your Role
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-390">{role}</p>
          </div>
        </div>

        <div className={`border ${accentBorder} rounded-lg overflow-hidden`} data-id="element-391">
          <div className={`${accentBg} px-3 py-2 text-center border-b ${accentBorder}`} data-id="element-392">
            <h4 className={`text-[11px] font-bold ${accent} uppercase tracking-wider`} data-id="element-393">
              Compensation Structure
            </h4>
            <p className="text-[9px] text-gray-500 uppercase mt-0.5 tracking-wider" data-id="element-394">
              Commission on every loan closed (over 9 closings)
            </p>
          </div>
          <table className="w-full text-sm" data-id="element-395">
            <thead className={`${headerBg} text-white text-[10px]`} data-id="element-396">
              <tr data-id="element-397">
                <th className="py-2 px-3 text-left font-bold uppercase tracking-wider" data-id="element-398">
                  Loans (6 mo)
                </th>
                <th className="py-2 px-3 text-right font-bold uppercase tracking-wider" data-id="element-399">
                  Incentive
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium text-[#0A1F5C]" data-id="element-400">
              {rows.map((row, idx) => <tr key={idx} className={row.highlight ? accentBg : ''} data-id="element-401">
                  <td className={`py-2 px-3 ${row.highlight ? 'font-bold' : ''}`} data-id="element-402">
                    {row.range}
                  </td>
                  <td className={`py-2 px-3 text-right tabular-nums ${row.incentive === '$0' ? 'text-gray-400' : accent} ${row.highlight ? 'font-bold' : ''}`} data-id="element-403">
                    {row.incentive}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {growthCallout && <div className={`${accentBg} rounded-lg p-3 flex gap-3 items-center border ${accentBorder}`} data-id="element-404">
            <TrendingUpIcon className={`w-6 h-6 ${accent} shrink-0`} data-id="element-405" />
            <div data-id="element-406">
              <h4 className={`text-[10px] font-bold ${accent} uppercase tracking-wider mb-0.5`} data-id="element-407">
                {growthCallout.title}
              </h4>
              <p className="text-xs font-bold text-[#0A1F5C]" data-id="element-408">
                {growthCallout.subtitle}
              </p>
            </div>
          </div>}

        <div className="mt-auto bg-gray-50 rounded-lg p-3 flex gap-3 items-start border border-gray-200" data-id="element-409">
          <div className={`${headerBg} p-2 rounded-full text-white shrink-0`} data-id="element-410">
            <TrophyIcon className="w-4 h-4" data-id="element-411" />
          </div>
          <div data-id="element-412">
            <h4 className={`text-[10px] font-bold ${accent} uppercase tracking-wider mb-0.5`} data-id="element-413">
              Focus
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-414">{focus}</p>
          </div>
        </div>
      </div>
    </div>;
}
export function RankComparison() {
  return <motion.section initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: 0.7
  }} className="pt-4" data-id="element-415">
      <h2 className="text-2xl font-black text-[#0A1F5C] uppercase tracking-widest text-center mb-8" data-id="element-416">
        Processor Roadmap
      </h2>

      <div className="flex flex-col lg:flex-row gap-6 items-stretch" data-id="element-417">
        <RankCard badge="Current Role" badgeColor="bg-white/15 text-white" headerBg="bg-[#0A1F5C]" accent="text-[#2563EB]" accentBg="bg-blue-50" accentBorder="border-blue-200" icon={<UserIcon className="w-6 h-6" data-id="element-419" />} title="Loan Processor" role="You are the foundation of a smooth closing experience. Accuracy, efficiency, and ownership drive every successful loan." rows={[{
        range: '1 – 9 Loans',
        incentive: '$0'
      }, {
        range: '10 – 14 Loans',
        incentive: '$15'
      }, {
        range: '15 – 19 Loans',
        incentive: '$25'
      }, {
        range: '20 – 24 Loans',
        incentive: '$35',
        highlight: true
      }, {
        range: '25+ Loans',
        incentive: '$45'
      }]} focus="Master your process. Own the details. Deliver excellence in every file." data-id="element-418" />

        <div className="flex lg:items-center justify-center" data-id="element-420">
          <div className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center" data-id="element-421">
            <ArrowRightIcon className="w-5 h-5 text-[#2563EB] rotate-90 lg:rotate-0" data-id="element-422" />
          </div>
        </div>

        <RankCard badge="Next Level" badgeColor="bg-white/20 text-white" headerBg="bg-[#E8801F]" accent="text-[#E8801F]" accentBg="bg-orange-50" accentBorder="border-orange-200" icon={<StarIcon className="w-6 h-6 fill-white" data-id="element-424" />} title="Senior Processor" role="You lead by example. You bring expertise, guide others, and ensure top-tier results across every loan." rows={[{
        range: '1 – 9 Loans',
        incentive: '$0'
      }, {
        range: '10 – 14 Loans',
        incentive: '$25'
      }, {
        range: '15 – 19 Loans',
        incentive: '$35'
      }, {
        range: '20 – 24 Loans',
        incentive: '$45'
      }, {
        range: '25+ Loans',
        incentive: '$55',
        highlight: true
      }]} focus="Lead with excellence. Strengthen your team. Elevate the experience." growthCallout={{
        title: 'Growth Benefits',
        subtitle: 'Higher incentive plan at every level'
      }} data-id="element-423" />
      </div>
    </motion.section>;
}